// Pending (and recent) scheduled bulk SMS — view and cancel before they send.
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, CalendarClock, Loader2, Trash2, CheckCircle2, Ban, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const StatusTag = ({ s }) => {
  const map = {
    scheduled: ['bg-indigo-50 text-indigo-700', CalendarClock, 'Scheduled'],
    sent: ['bg-emerald-50 text-emerald-700', CheckCircle2, 'Sent'],
    cancelled: ['bg-slate-100 text-slate-500', Ban, 'Cancelled'],
    failed: ['bg-rose-50 text-rose-600', AlertTriangle, 'Failed'],
    sending: ['bg-amber-50 text-amber-700', Loader2, 'Sending'],
  };
  const [cls, Icon, text] = map[s] || map.scheduled;
  return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-black ${cls}`}><Icon className="w-3 h-3" />{text}</span>;
};

export default function ScheduledSmsPanel({ open, onClose, headers, onChanged, endpoint = '/api/onboarding/bulk-sms/scheduled', noun = ['client', 'clients'] }) {
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState([]);
  const [recent, setRecent] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}${endpoint}`, { headers });
      setPending(data.scheduled || []);
      setRecent((data.recent || []).filter((r) => r.status !== 'scheduled'));
    } catch { toast.error('Could not load scheduled sends'); }
    finally { setLoading(false); }
  }, [headers, endpoint]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const cancel = async (id) => {
    if (!window.confirm('Cancel this scheduled bulk SMS?')) return;
    try {
      await axios.delete(`${API}${endpoint}/${id}`, { headers });
      toast.success('Scheduled send cancelled');
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not cancel'); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-slate-900/50 backdrop-blur-sm p-0 sm:p-4" data-testid="scheduled-sms-panel" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full sm:max-w-xl bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[88vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
          <span className="flex items-center gap-2 font-black text-slate-900"><CalendarClock className="w-4 h-4 text-[#15133A]" /> Scheduled bulk SMS</span>
          <button type="button" onClick={onClose} data-testid="scheduled-sms-close" className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {loading ? <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-300" /></div> : (
            <>
              <div>
                <p className="mb-2 text-xs font-black uppercase tracking-wide text-slate-400">Upcoming</p>
                {!pending.length ? <p className="text-sm text-slate-400 py-4 text-center">No scheduled sends pending.</p> : (
                  <ul className="space-y-2">
                    {pending.map((s) => (
                      <li key={s.id} className="rounded-2xl border border-indigo-100 bg-indigo-50/40 p-3.5" data-testid={`scheduled-row-${s.id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2"><span className="text-sm font-black text-[#15133A]">{s.scheduled_at_label}</span><StatusTag s={s.status} /></div>
                            <p className="mt-0.5 text-[11px] text-slate-500">{s.recipient_count} {s.recipient_count === 1 ? noun[0] : noun[1]}{s.template_name ? ` · ${s.template_name}` : ''}</p>
                            <p className="mt-1 line-clamp-2 text-[12px] text-slate-600">{s.body}</p>
                          </div>
                          <button type="button" onClick={() => cancel(s.id)} data-testid={`scheduled-cancel-${s.id}`} className="shrink-0 inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-white px-2.5 py-1.5 text-[11px] font-bold text-rose-600 hover:bg-rose-50"><Trash2 className="w-3.5 h-3.5" /> Cancel</button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {recent.length > 0 && (
                <div>
                  <p className="mb-2 text-xs font-black uppercase tracking-wide text-slate-400">Recent</p>
                  <ul className="space-y-1.5">
                    {recent.slice(0, 20).map((s) => (
                      <li key={s.id} className="flex items-center gap-2 rounded-xl border border-slate-100 px-3 py-2" data-testid={`scheduled-recent-${s.id}`}>
                        <span className="min-w-0 flex-1 text-[12px] font-semibold text-slate-700 truncate">{s.scheduled_at_label} · {s.recipient_count} {s.recipient_count === 1 ? noun[0] : noun[1]}</span>
                        {s.status === 'sent' && s.result && <span className="text-[11px] text-slate-400">{s.result.delivered}/{s.result.selected} delivered</span>}
                        <StatusTag s={s.status} />
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
